import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { updateSucursal, type Sucursal } from '../../services/pos/sucursalService';
import { AxiosError } from 'axios';

interface EditSucursalPageProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    sucursal: Sucursal;
}

const EditSucursalPage: React.FC<EditSucursalPageProps> = ({
    isOpen,
    onClose,
    onSuccess,
    sucursal
}) => {
    const [formData, setFormData] = useState({
        nombre: '',
        direccion: '',
        codigo: '',
        telefono: '',
        email: '',
        activo: true
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (sucursal) {
            setFormData({
                nombre: sucursal.nombre || '',
                direccion: sucursal.direccion || '',
                codigo: sucursal.codigo || '',
                telefono: sucursal.telefono || '',
                email: sucursal.email || '',
                activo: sucursal.activo ?? true
            });
        }
    }, [sucursal]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setError(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (!formData.nombre.trim()) throw new Error('El nombre es requerido');
            if (!formData.direccion.trim()) throw new Error('La dirección es requerida');
            if (!formData.codigo.trim()) throw new Error('El código es requerido');

            if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
                throw new Error('El email no es válido');
            }

            const sucursalData = {
                nombre: formData.nombre.trim(),
                direccion: formData.direccion.trim(),
                codigo: formData.codigo.trim().toUpperCase(),
                telefono: formData.telefono.trim() || null,
                email: formData.email.trim() || null,
                activo: formData.activo
            };

            await updateSucursal(sucursal.id, sucursalData);
            onSuccess();
            onClose();
        } catch (err: unknown) {
            if (err instanceof AxiosError && err.response?.data) {
                const errorData = err.response.data;
                const messages = typeof errorData === 'object' 
                    ? Object.values(errorData).flat() 
                    : [String(errorData)];
                setError(messages.join(', ') || 'Error al actualizar');
            } else {
                setError(err instanceof Error ? err.message : 'Error desconocido');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setError(null);
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
            
            <LumaAccessiblePanel data-luma-dialog="true" className="bg-white rounded-xl w-full max-w-2xl mx-4 border border-gray-100 shadow-2xl relative overflow-hidden">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-100">
                    <div>
                        <LumaAccessibleTitle className="text-xl font-bold text-gray-900">Editar Sucursal</LumaAccessibleTitle>
                    </div>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                    <div className="p-6">
                        {error && (
                            <div className="mb-6 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg">
                                {error}
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Columna Izquierda */}
                            <div className="space-y-4">
                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Identificación</h4>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                                    <input
                                        name="nombre"
                                        value={formData.nombre}
                                        onChange={handleChange}
                                        disabled={loading}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:bg-gray-50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                                    <input
                                        name="codigo"
                                        value={formData.codigo}
                                        onChange={handleChange}
                                        disabled={loading}
                                        required
                                        maxLength={10}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none uppercase disabled:bg-gray-50"
                                    />
                                </div>
                            </div>

                            {/* Columna Derecha */}
                            <div className="space-y-4">
                                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Contacto</h4>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                                    <input
                                        name="telefono"
                                        value={formData.telefono}
                                        onChange={handleChange}
                                        disabled={loading}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        disabled={loading}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                            </div>

                            {/* Ancho Completo */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Dirección Completa *</label>
                                <input
                                    name="direccion"
                                    value={formData.direccion}
                                    onChange={handleChange}
                                    disabled={loading}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>

                            {/* Toggle de Estado */}
                            <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg flex items-center justify-between border border-gray-100">
                                <div>
                                    <span className="block text-sm font-medium text-gray-800">Estado de la Sucursal</span>
                                    <p className="text-xs text-gray-500">
                                        {formData.activo ? 'La sucursal está operando normalmente' : 'La sucursal está marcada como inactiva'}
                                    </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="activo"
                                        className="sr-only"
                                        checked={formData.activo}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                    <div className={`w-11 h-6 rounded-full transition-colors ${formData.activo ? 'bg-[#3564db]' : 'bg-gray-300'}`}>
                                        <div className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow transition-transform ${formData.activo ? 'translate-x-5' : 'translate-x-0'}`} />
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-3 p-6 bg-gray-50 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={loading}
                            className="px-5 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 text-sm bg-[#3564db] hover:bg-[#8f1f64] text-white rounded-lg transition-all shadow-md font-bold flex items-center gap-2"
                        >
                            {loading && (
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            )}
                            {loading ? 'Guardando...' : 'Actualizar Cambios'}
                        </button>
                    </div>
                </form>
            </LumaAccessiblePanel>
        </LumaAccessibleDialog>
    );
};

export default EditSucursalPage;
